import React, { useMemo } from "react";

import {
  EmailEditorProvider,
  EmailEditorProviderProps,
  IEmailTemplate,
  EmailEditor,
} from "easy-email-editor";
import {
  BlockManager,
  BasicType,
  IPage,
  JsonToMjml,
} from "easy-email-core";
import {
  BlockAttributeConfigurationManager,
  StandardLayout,
} from "easy-email-extensions";
import mjml from "mjml-browser";
import {
  ImageAttributePanel,
  ImagePickerContextProvider,
} from "./ImageAttributePanel";

import "easy-email-editor/lib/style.css";
import "easy-email-extensions/lib/style.css";
import "@arco-design/web-react/dist/css/arco.css";

BlockAttributeConfigurationManager.add({
  [BasicType.IMAGE]: ImageAttributePanel,
});

const FONT_LIST = [
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "'Arial Black', Arial, sans-serif", label: "Arial Black" },
  { value: "'Arial Narrow', Arial, sans-serif", label: "Arial Narrow" },
  { value: "Baskerville, 'Baskerville Old Face', serif", label: "Baskerville" },
  { value: "'Book Antiqua', Palatino, serif", label: "Book Antiqua" },
  { value: "'Bookman Old Style', serif", label: "Bookman Old Style" },
  { value: "'Brush Script MT', cursive", label: "Brush Script MT" },
  { value: "Calibri, 'Trebuchet MS', sans-serif", label: "Calibri" },
  { value: "Cambria, Georgia, serif", label: "Cambria" },
  { value: "Candara, Calibri, sans-serif", label: "Candara" },
  { value: "'Century Gothic', sans-serif", label: "Century Gothic" },
  { value: "'Comic Sans MS', cursive", label: "Comic Sans MS" },
  { value: "Consolas, 'Courier New', monospace", label: "Consolas" },
  { value: "Constantia, Georgia, serif", label: "Constantia" },
  { value: "'Copperplate Gothic', serif", label: "Copperplate Gothic" },
  { value: "'Courier New', Courier, monospace", label: "Courier New" },
  { value: "'Didot', 'Didot LT STD', serif", label: "Didot" },
  { value: "Garamond, 'Times New Roman', serif", label: "Garamond" },
  { value: "Geneva, Verdana, sans-serif", label: "Geneva" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "'Gill Sans', 'Gill Sans MT', sans-serif", label: "Gill Sans" },
  { value: "Helvetica, Arial, sans-serif", label: "Helvetica" },
  { value: "'Helvetica Neue', Helvetica, Arial, sans-serif", label: "Helvetica Neue" },
  { value: "Impact, Charcoal, sans-serif", label: "Impact" },
  { value: "'Lucida Bright', Georgia, serif", label: "Lucida Bright" },
  { value: "'Lucida Console', Monaco, monospace", label: "Lucida Console" },
  { value: "'Lucida Sans', 'Lucida Sans Unicode', sans-serif", label: "Lucida Sans" },
  { value: "'Lucida Sans Unicode', 'Lucida Grande', sans-serif", label: "Lucida Sans Unicode" },
  { value: "Menlo, Monaco, monospace", label: "Menlo" },
  { value: "Monaco, 'Courier New', monospace", label: "Monaco" },
  { value: "'MS Sans Serif', Geneva, sans-serif", label: "MS Sans Serif" },
  { value: "'MS Serif', 'New York', serif", label: "MS Serif" },
  { value: "Optima, Candara, sans-serif", label: "Optima" },
  { value: "Palatino, 'Palatino Linotype', serif", label: "Palatino" },
  { value: "'Palatino Linotype', Palatino, serif", label: "Palatino Linotype" },
  { value: "Perpetua, serif", label: "Perpetua" },
  { value: "Rockwell, 'Courier Bold', serif", label: "Rockwell" },
  { value: "'Segoe UI', Tahoma, sans-serif", label: "Segoe UI" },
  { value: "Tahoma, Geneva, sans-serif", label: "Tahoma" },
  { value: "Times, 'Times New Roman', serif", label: "Times" },
  { value: "'Times New Roman', Times, serif", label: "Times New Roman" },
  { value: "'Trebuchet MS', Helvetica, sans-serif", label: "Trebuchet MS" },
  { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  // Google / web fonts commonly used in email — these will fall back to system fonts
  // in mail clients that don't support webfonts, but render correctly in modern ones.
  { value: "'Inter', sans-serif", label: "Inter" },
  { value: "'Roboto', sans-serif", label: "Roboto" },
  { value: "'Open Sans', sans-serif", label: "Open Sans" },
  { value: "'Lato', sans-serif", label: "Lato" },
  { value: "'Montserrat', sans-serif", label: "Montserrat" },
  { value: "'Source Sans Pro', sans-serif", label: "Source Sans Pro" },
  { value: "'Source Serif Pro', serif", label: "Source Serif Pro" },
  { value: "'Poppins', sans-serif", label: "Poppins" },
  { value: "'Nunito', sans-serif", label: "Nunito" },
  { value: "'Nunito Sans', sans-serif", label: "Nunito Sans" },
  { value: "'Raleway', sans-serif", label: "Raleway" },
  { value: "'Merriweather', serif", label: "Merriweather" },
  { value: "'Playfair Display', serif", label: "Playfair Display" },
  { value: "'PT Sans', sans-serif", label: "PT Sans" },
  { value: "'PT Serif', serif", label: "PT Serif" },
  { value: "'Oswald', sans-serif", label: "Oswald" },
  { value: "'Ubuntu', sans-serif", label: "Ubuntu" },
  { value: "'Fira Sans', sans-serif", label: "Fira Sans" },
  { value: "'Fira Code', monospace", label: "Fira Code" },
  { value: "'Work Sans', sans-serif", label: "Work Sans" },
  { value: "'Rubik', sans-serif", label: "Rubik" },
  { value: "'DM Sans', sans-serif", label: "DM Sans" },
  { value: "'DM Serif Display', serif", label: "DM Serif Display" },
  { value: "'Libre Baskerville', serif", label: "Libre Baskerville" },
  { value: "'Libre Franklin', sans-serif", label: "Libre Franklin" },
  { value: "'IBM Plex Sans', sans-serif", label: "IBM Plex Sans" },
  { value: "'IBM Plex Serif', serif", label: "IBM Plex Serif" },
  { value: "'IBM Plex Mono', monospace", label: "IBM Plex Mono" },
  { value: "'JetBrains Mono', monospace", label: "JetBrains Mono" },
  { value: "'Space Grotesk', sans-serif", label: "Space Grotesk" },
  { value: "'Space Mono', monospace", label: "Space Mono" },
  { value: "'Karla', sans-serif", label: "Karla" },
  { value: "'Manrope', sans-serif", label: "Manrope" },
  { value: "'Quicksand', sans-serif", label: "Quicksand" },
  { value: "'Mulish', sans-serif", label: "Mulish" },
  { value: "'Bebas Neue', sans-serif", label: "Bebas Neue" },
  { value: "'Dancing Script', cursive", label: "Dancing Script" },
  { value: "'Pacifico', cursive", label: "Pacifico" },
  { value: "'Caveat', cursive", label: "Caveat" },
  { value: "'Lora', serif", label: "Lora" },
  { value: "'Crimson Text', serif", label: "Crimson Text" },
  { value: "'EB Garamond', serif", label: "EB Garamond" },
];

export type EmailBuilderTemplate = IEmailTemplate;

export function createEmptyTemplate(subject = "Untitled"): EmailBuilderTemplate {
  const page = BlockManager.getBlockByType(BasicType.PAGE)?.create({}) as IPage;
  return {
    subject,
    subTitle: "",
    content: page,
  };
}

export function renderTemplateToMjml(template: EmailBuilderTemplate): string {
  return JsonToMjml({
    data: template.content,
    mode: "production",
    context: template.content,
  });
}

export function renderTemplateToHtml(template: EmailBuilderTemplate): string {
  const mjmlString = renderTemplateToMjml(template);
  const { html } = mjml(mjmlString, { validationLevel: "soft" });
  return html;
}

interface EmailBuilderProps {
  initialTemplate?: EmailBuilderTemplate | null;
  subject?: string;
  onChange?: (template: EmailBuilderTemplate) => void;
  height?: string;
  getToken?: () => Promise<string>;
  companyId?: string;
}

const EmailBuilder: React.FC<EmailBuilderProps> = ({
  initialTemplate,
  subject,
  onChange,
  height = "calc(100vh - 64px)",
  getToken,
  companyId,
}) => {
  const initialValues = useMemo<EmailBuilderTemplate>(() => {
    if (initialTemplate?.content) return initialTemplate;
    return createEmptyTemplate(subject || "Untitled");
  }, [initialTemplate, subject]);

  const imagePickerCtx = useMemo(
    () => (getToken && companyId ? { getToken, companyId } : null),
    [getToken, companyId]
  );

  const tree = (
    <>
      <div
        id="FIXED_CONTAINER_ID"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 2000,
          pointerEvents: "none",
        }}
      />
      <EmailEditorProvider
      data={initialValues}
      height={height}
      autoComplete
      dashed={false}
      fontList={FONT_LIST}
    >
      {(values) => {
        if (onChange) onChange(values.values as EmailBuilderTemplate);
        return (
          <StandardLayout
            categories={[
              {
                label: "Content",
                active: true,
                blocks: [
                  { type: BasicType.TEXT },
                  { type: BasicType.IMAGE },
                  { type: BasicType.BUTTON },
                  { type: BasicType.SPACER },
                  { type: BasicType.DIVIDER },
                  { type: BasicType.NAVBAR },
                  { type: BasicType.SOCIAL },
                  { type: BasicType.HERO },
                  { type: BasicType.WRAPPER },
                  // {{unsubscribe}} resolves to a per-recipient link at send time
                  // (see backend marketing_campaign_storage). Pre-styled muted so it
                  // reads as a footer link by default; the user can restyle freely.
                  {
                    type: BasicType.TEXT,
                    title: "Unsubscribe",
                    payload: {
                      attributes: {
                        align: "center",
                        color: "#888888",
                        "font-size": "12px",
                        padding: "20px 25px 20px 25px",
                      },
                      data: {
                        value: {
                          content:
                            '<a href="{{unsubscribe}}" style="color:#888888;text-decoration:underline;">Unsubscribe</a>',
                        },
                      },
                    },
                  },
                ],
              },
              {
                label: "Layout",
                active: true,
                displayType: "column",
                blocks: [
                  {
                    title: "1 column",
                    payload: [["100%"]],
                  },
                  {
                    title: "2 columns",
                    payload: [
                      ["50%", "50%"],
                      ["33%", "67%"],
                      ["67%", "33%"],
                    ],
                  },
                  {
                    title: "3 columns",
                    payload: [["33.33%", "33.33%", "33.33%"]],
                  },
                  {
                    title: "4 columns",
                    payload: [["25%", "25%", "25%", "25%"]],
                  },
                ],
              },
            ]}
          >
            <EmailEditor />
          </StandardLayout>
        );
      }}
    </EmailEditorProvider>
    </>
  );

  if (imagePickerCtx) {
    return (
      <ImagePickerContextProvider value={imagePickerCtx}>
        {tree}
      </ImagePickerContextProvider>
    );
  }
  return tree;
};

export default EmailBuilder;
